import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router';
import type { ApiError } from '../api';
import { endpoints, when } from '../api';
import { storeBundle, storedBundles, wipePerformance } from '../offline';
import type { StoredBundle } from '../offline';
import { recordClockOffset, useScannerContext, useSelection, useSession } from '../session';
import { Button, ErrorNote, FullPageMessage } from '../ui';
import { PLATFORM_NAME } from '@bre-ticketing/contracts';
import { dateWords } from '../terms';

/**
 * Before the doors (docs/scanner.md §1): pick tonight's performance(s) and
 * the gate, download the offline bundle for each (with scan.offline_bundle),
 * choose camera or hardware scanner, and start. Without a network the last
 * selection and the bundles already on the device still work.
 */
export default function SetupPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const session = useSession();
  const context = useScannerContext();
  const { selection, setSelection } = useSelection();
  const [performanceIds, setPerformanceIds] = useState<string[]>(selection?.performance_ids ?? []);
  const [gateId, setGateId] = useState<string | null>(selection?.gate_id ?? null);
  const [input, setInput] = useState<'CAMERA' | 'HID_SCANNER'>(selection?.input ?? 'CAMERA');
  const [bundles, setBundles] = useState<StoredBundle[]>([]);
  const [error, setError] = useState<string | null>(null);
  const refreshBundles = () => void storedBundles().then(setBundles);
  useEffect(refreshBundles, []);
  useEffect(() => {
    if (context.data) recordClockOffset(context.data.server_time);
  }, [context.data]);

  const download = useMutation({
    mutationFn: async (performanceId: string) => storeBundle((await endpoints.bundle(performanceId)).bundle),
    onSuccess: () => {
      setError(null);
      refreshBundles();
    },
    onError: (cause: ApiError) => setError(cause.message),
  });
  const signOut = useMutation({
    mutationFn: endpoints.logout,
    onSettled: async () => {
      queryClient.clear();
      await navigate('/login', { replace: true });
    },
  });

  const performances = context.data?.performances ?? [];
  const gates = context.data?.gates ?? [];
  const offline = context.error?.isOffline ?? false;
  const toggle = (id: string) => setPerformanceIds((ids) => (ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id]));
  const start = () => {
    if (performanceIds.length === 0) return;
    setSelection({ performance_ids: performanceIds, gate_id: gateId, gate_name: gates.find((g) => g.id === gateId)?.name ?? selection?.gate_name ?? null, input });
    void navigate('/scan');
  };

  if (context.isPending && bundles.length === 0) return <FullPageMessage title={`Loading today's ${dateWords().many}…`} />;

  return (
    <div className="safe-top safe-bottom mx-auto flex min-h-full max-w-lg flex-col gap-5 p-5">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold">
            <span className="font-display font-bold tracking-tight">{PLATFORM_NAME}</span> <span className="text-gold">Scanner</span>
          </h1>
          <p className="text-sm text-white/60">
            {context.data?.device.name ?? 'This device'} · {session.data?.staff.display_name ?? ''}
          </p>
        </div>
        <Button variant="ghost" className="text-sm" onClick={() => signOut.mutate()}>
          Sign out
        </Button>
      </header>
      {error ? <ErrorNote>{error}</ErrorNote> : null}
      {offline ? <ErrorNote>No connection. Only {dateWords().many} with a bundle on this device can be scanned until it returns.</ErrorNote> : null}

      <section>
        <h2 className="text-xs font-semibold tracking-wide text-white/50 uppercase">{dateWords().Many}</h2>
        <ul className="mt-2 space-y-2">
          {(offline ? bundles.map((b) => ({ id: b.performance_id, title: b.performance.title, starts_at: b.performance.starts_at, timezone: b.performance.timezone, counters: null })) : performances).map((p) => {
            const bundle = bundles.find((b) => b.performance_id === p.id);
            const selected = performanceIds.includes(p.id);
            return (
              <li key={p.id} className={`rounded-2xl border p-4 ${selected ? 'border-gold bg-shell-800' : 'border-shell-700 bg-shell-800/60'}`}>
                <button type="button" onClick={() => toggle(p.id)} className="flex w-full items-start justify-between gap-3 text-left">
                  <span>
                    <span className="block font-semibold">{p.title}</span>
                    <span className="block text-sm text-white/60">{when(p.starts_at, p.timezone, 'long')}</span>
                    {'counters' in p && p.counters ? <span className="tabular block text-xs text-white/50">{p.counters.performance_admitted} of {p.counters.performance_live_tickets} admitted</span> : null}
                  </span>
                  <span className={`mt-1 inline-block h-6 w-6 rounded-full border-2 ${selected ? 'border-gold bg-gold' : 'border-shell-500'}`} aria-hidden="true" />
                </button>
                <div className="mt-3 flex items-center justify-between gap-2 text-xs text-white/60">
                  <span>{bundle ? `Offline bundle v${bundle.bundle_version} · ${bundle.ticket_count} tickets · synced ${when(bundle.synced_at, undefined, 'time')}` : 'No offline bundle on this device'}</span>
                  {context.data?.can_bundle && !offline ? (
                    <Button className="min-h-11 px-3 text-sm" disabled={download.isPending} onClick={() => download.mutate(p.id)}>
                      {bundle ? 'Refresh' : 'Download'}
                    </Button>
                  ) : null}
                  {bundle && offline ? (
                    <Button className="min-h-11 px-3 text-sm" onClick={() => void wipePerformance(p.id).then(refreshBundles)}>
                      Remove
                    </Button>
                  ) : null}
                </div>
              </li>
            );
          })}
          {performances.length === 0 && bundles.length === 0 ? <li className="rounded-2xl border border-shell-700 p-4 text-sm text-white/60">Nothing is scheduled at this venue in the next month.</li> : null}
        </ul>
      </section>

      <section>
        <h2 className="text-xs font-semibold tracking-wide text-white/50 uppercase">Gate</h2>
        <div className="mt-2 flex flex-wrap gap-2">
          <Button variant={gateId === null ? 'primary' : 'secondary'} onClick={() => setGateId(null)}>
            Any gate
          </Button>
          {gates.map((g) => (
            <Button key={g.id} variant={gateId === g.id ? 'primary' : 'secondary'} onClick={() => setGateId(g.id)}>
              {g.name}
            </Button>
          ))}
        </div>
      </section>

      <section>
        <h2 className="text-xs font-semibold tracking-wide text-white/50 uppercase">Input</h2>
        <div className="mt-2 flex gap-2">
          <Button variant={input === 'CAMERA' ? 'primary' : 'secondary'} onClick={() => setInput('CAMERA')}>
            Camera
          </Button>
          <Button variant={input === 'HID_SCANNER' ? 'primary' : 'secondary'} onClick={() => setInput('HID_SCANNER')}>
            Hardware scanner
          </Button>
        </div>
      </section>

      <Button variant="primary" className="mt-auto w-full text-lg" disabled={performanceIds.length === 0} onClick={start}>
        Start scanning
      </Button>
    </div>
  );
}
